import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Package, Tag, ImagePlus, EyeOff, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { FUEL_TYPES } from '../../mock/mock';
import { toFuelList } from '../../lib/fuels';
import { api, fileURL } from '../../lib/api';
import { toast } from 'sonner';

const AdminProducts = () => {
  const [mappings, setMappings] = useState([]);
  const [brands, setBrands] = useState([]);
  const [master, setMaster] = useState(FUEL_TYPES);
  const [newProduct, setNewProduct] = useState('');
  const [addingProduct, setAddingProduct] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openBrand, setOpenBrand] = useState(false);
  const [newBrand, setNewBrand] = useState('');
  const [newBrandOwn, setNewBrandOwn] = useState(false);
  const [form, setForm] = useState({ brand: '', master_id: 'super', name: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [m, b, mp] = await Promise.all([
        api.get('/products/mappings'),
        api.get('/brands'),
        api.get('/products/master').catch(() => ({ data: null })),
      ]);
      setMappings(m.data);
      setBrands(b.data);
      if (mp.data && mp.data.length) setMaster(toFuelList(mp.data));
      if (!form.brand && b.data[0]) setForm(f => ({ ...f, brand: b.data[0].name }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.brand || !form.name) { toast.error('Marca y nombre requeridos'); return; }
    try {
      await api.post('/products/mappings', form);
      toast.success('Mapeo guardado');
      setOpen(false);
      setForm({ brand: brands[0]?.name || '', master_id: 'super', name: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este mapeo?')) return;
    try { await api.delete(`/products/mappings/${id}`); toast('Eliminado'); load(); }
    catch (e) { toast.error('Error'); }
  };

  const addBrand = async () => {
    const name = newBrand.trim();
    if (!name) { toast.error('Nombre vacío'); return; }
    try {
      await api.post('/brands', { name, is_own: newBrandOwn });
      toast.success(`Marca "${name}" agregada${newBrandOwn ? ' como propia' : ''}`);
      setOpenBrand(false); setNewBrand(''); setNewBrandOwn(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addProduct = async () => {
    const name = newProduct.trim();
    if (!name) { toast.error('Nombre vacío'); return; }
    setAddingProduct(true);
    try {
      const r = await api.post('/products/master', { name });
      toast.success(`Producto "${name}" agregado · ${r.data.stations_updated} estación(es) actualizadas`);
      setNewProduct('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setAddingProduct(false); }
  };

  const removeProduct = async (ft) => {
    if (!window.confirm(`¿Eliminar el producto "${ft.name}" del catálogo global?\n\nSe quitará EN CASCADA de todas las estaciones (productos disponibles, precios por estación) y sus mapeos de marca.`)) return;
    try {
      const r = await api.delete(`/products/master/${ft.id}`);
      toast.success(`"${ft.name}" eliminado · ${r.data.stations_updated} estación(es) actualizadas`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleCompetitors = async (ft, action, scope = 'competitors') => {
    const verb = action === 'disable' ? 'DESACTIVAR' : 'ACTIVAR';
    const scopeTxt = scope === 'all' ? 'TODAS las estaciones (propias + competencia)' : 'TODAS las estaciones competidoras';
    const extra = scope === 'all'
      ? '\n\nEl producto NO se elimina del catálogo: solo se destilda en cada estación. Luego puedes activarlo a mano en las que sí lo venden (AD-P06).'
      : '\n\nLas estaciones propias no se tocan. Cada estación puede afinarse individualmente después en AD-P06.';
    if (!window.confirm(`¿${verb} "${ft.name}" en ${scopeTxt}?${extra}`)) return;
    try {
      const r = await api.post(`/products/master/${ft.id}/competitors-toggle`, { action, scope });
      toast.success(`"${ft.name}" ${action === 'disable' ? 'desactivado' : 'activado'} en ${scope === 'all' ? 'todas las estaciones' : 'competidoras'} · ${r.data.updated} estación(es) actualizadas`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleOwn = async (b) => {
    try {
      const r = await api.patch(`/brands/${b.id}`, { is_own: !b.is_own });
      toast.success(`${b.name} ${r.data.is_own ? 'marcada como propia' : 'marcada como competencia'} · ${r.data.stations_updated} estaciones actualizadas`);
      const br = await api.get('/brands'); setBrands(br.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error actualizando la marca'); }
  };

  const uploadLogo = async (b, f) => {
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      await api.post(`/brands/${b.id}/logo`, fd);
      toast.success(`Logo de ${b.name} actualizado`);
      const r = await api.get('/brands'); setBrands(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error subiendo el logo'); }
  };

  const removeLogo = async (b) => {
    try {
      await api.delete(`/brands/${b.id}/logo`);
      const r = await api.get('/brands'); setBrands(r.data);
    } catch { toast.error('Error quitando el logo'); }
  };

  const removeBrand = async (b) => {
    if (!window.confirm(`¿Eliminar la marca "${b.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/brands/${b.id}`);
      toast.success(`Marca "${b.name}" eliminada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  // Group by brand
  const byBrand = mappings.reduce((acc, m) => {
    if (!acc[m.brand]) acc[m.brand] = {};
    acc[m.brand][m.master_id] = m;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Productos</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">Catálogo maestro y mapeo de nombres comerciales por marca.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setNewBrand(''); setOpenBrand(true); }}
            variant="outline"
            className="h-10 rounded-full border-[#1c1c1c]/20 text-[12.5px]"
            data-testid="open-add-brand-dialog"
          >
            <Tag size={14} className="mr-1.5"/> Nueva marca
          </Button>
          <Button onClick={() => setOpen(true)} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="open-add-mapping-dialog">
            <Plus size={14} className="mr-1.5"/> Nuevo mapeo
          </Button>
        </div>
      </div>

      {/* Master catalog */}
      <div className="bg-white border border-[#e8e5de] rounded-xl p-5" data-testid="master-catalog-card">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Package size={15} className="text-[#8a8579]"/>
            <h3 className="text-[14px] font-medium tracking-tight">Catálogo global de productos</h3>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={newProduct}
              onChange={e => setNewProduct(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addProduct(); }}
              placeholder="Nuevo producto… (ej. GLP)"
              className="h-9 w-48 bg-[#f8f6f1] text-[12.5px]"
              data-testid="new-master-product-input"
            />
            <Button onClick={addProduct} disabled={addingProduct} className="h-9 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12px]" data-testid="add-master-product-btn">
              {addingProduct ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13} className="mr-1"/>}Agregar
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-[#8a8579] mb-4">Al agregar un producto se habilita en todas las estaciones; al eliminarlo se quita en cascada de estaciones, precios y mapeos. Con los íconos <EyeOff size={10} className="inline -mt-0.5"/>/<Eye size={10} className="inline -mt-0.5"/> puedes desactivar/activar un producto en TODAS las competidoras de un clic (las propias no se tocan). Cada estación puede afinar sus productos en AD-P06.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {master.map(ft => (
            <div key={ft.id} className="group flex items-center gap-2.5 p-3 bg-[#f8f6f1] rounded-lg border border-[#e8e5de]" data-testid={`master-product-${ft.id}`}>
              <span className="w-1.5 h-8 rounded-full" style={{ background: ft.color }}/>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium tracking-tight">{ft.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#8a8579] font-mono">{ft.id}</p>
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={() => toggleCompetitors(ft, 'disable')}
                  className="w-6 h-6 rounded hover:bg-[#fef3c7] inline-flex items-center justify-center text-[#92400e]"
                  title={`Desactivar "${ft.name}" en TODAS las competidoras`}
                  data-testid={`disable-competitors-${ft.id}`}
                >
                  <EyeOff size={12}/>
                </button>
                <button
                  onClick={() => toggleCompetitors(ft, 'enable')}
                  className="w-6 h-6 rounded hover:bg-[#dcfce7] inline-flex items-center justify-center text-[#166534]"
                  title={`Activar "${ft.name}" en TODAS las competidoras`}
                  data-testid={`enable-competitors-${ft.id}`}
                >
                  <Eye size={12}/>
                </button>
                <button
                  onClick={() => toggleCompetitors(ft, 'disable', 'all')}
                  className="w-6 h-6 rounded hover:bg-[#fee2e2] inline-flex items-center justify-center text-[#991b1b]"
                  title={`Quitar "${ft.name}" de TODAS las estaciones (propias + competencia) sin borrarlo del catálogo`}
                  data-testid={`disable-all-${ft.id}`}
                >
                  <EyeOff size={12} strokeWidth={2.6}/>
                </button>
                <button
                  onClick={() => removeProduct(ft)}
                  className="w-6 h-6 rounded hover:bg-[#fee2e2] inline-flex items-center justify-center text-[#991b1b]"
                  title={`Eliminar "${ft.name}" del catálogo (cascada)`}
                  data-testid={`delete-master-product-${ft.id}`}
                >
                  <Trash2 size={12}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mappings by brand */}
      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e8e5de]">
          <h3 className="text-[14px] font-medium tracking-tight">Nombres por marca</h3>
          <p className="text-[11px] text-[#8a8579] mt-0.5">Cuando un capturador está en una estación, verá estos nombres para identificar el combustible correctamente.</p>
        </div>
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#f8f6f1]">
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="py-3 px-4 font-normal">Marca</th>
                <th className="py-3 px-2 font-normal">Logo</th>
                {master.map(ft => <th key={ft.id} className="py-3 px-2 font-normal">{ft.name}</th>)}
                <th className="py-3 px-2 font-normal text-right">·</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {brands.map(b => (
                <tr key={b.id} className="hover:bg-[#fafaf6]" data-testid={`brand-row-${b.name}`}>
                  <td className="py-3 px-4 font-medium">
                    <div className="flex items-center gap-2">
                      {b.name}
                      <button onClick={() => toggleOwn(b)} title={b.is_own ? 'Cambiar a competencia' : 'Marcar como propia'}
                        className={`text-[9.5px] px-1.5 py-0.5 rounded-full border transition ${b.is_own ? 'bg-[#dcfce7] text-[#166534] border-[#bbf7d0]' : 'bg-white text-[#a8a397] border-[#e8e5de] hover:border-[#8a8579] hover:text-[#5a5a5a]'}`}
                        data-testid={`toggle-own-${b.name}`}>
                        {b.is_own ? 'propia' : 'competencia'}
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-1.5 group/logo">
                      {b.logo_url ? (
                        <>
                          <img src={fileURL(b.logo_url)} alt={b.name} className="w-8 h-8 rounded-full object-contain bg-white border border-[#e8e5de]" data-testid={`brand-logo-${b.name}`}/>
                          <button onClick={() => removeLogo(b)} className="opacity-0 group-hover/logo:opacity-100 text-[#991b1b] transition" title="Quitar logo" data-testid={`remove-logo-${b.name}`}><Trash2 size={11}/></button>
                        </>
                      ) : (
                        <label className="w-8 h-8 rounded-full border border-dashed border-[#cfcabe] flex items-center justify-center cursor-pointer hover:border-[#1c1c1c] hover:text-[#1c1c1c] text-[#a8a397] transition" title="Subir logo">
                          <ImagePlus size={13}/>
                          <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid={`upload-logo-${b.name}`}
                            onChange={e => { uploadLogo(b, e.target.files?.[0]); e.target.value=''; }}/>
                        </label>
                      )}
                    </div>
                  </td>
                  {FUEL_TYPES.map(ft => {
                    const m = byBrand[b.name]?.[ft.id];
                    return (
                      <td key={ft.id} className="py-3 px-2">
                        {m ? (
                          <span className="inline-flex items-center gap-2 group">
                            <span className="text-[12.5px]">{m.name}</span>
                            <button onClick={() => remove(m.id)} className="opacity-0 group-hover:opacity-100 text-[#991b1b] transition"><Trash2 size={11}/></button>
                          </span>
                        ) : (
                          <button onClick={() => { setForm({ brand: b.name, master_id: ft.id, name: '' }); setOpen(true); }} className="text-[11px] text-[#8a8579] hover:text-[#1c1c1c] underline-offset-2 hover:underline">
                            — agregar
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-3 px-2 text-right">
                    <button
                      onClick={() => removeBrand(b)}
                      className="w-7 h-7 rounded hover:bg-[#fee2e2] inline-flex items-center justify-center text-[#991b1b]"
                      title={`Eliminar marca "${b.name}"`}
                      data-testid={`delete-brand-${b.name}`}
                    >
                      <Trash2 size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
              {brands.length === 0 && (
                <tr><td colSpan={master.length + 3} className="py-8 text-center text-[#8a8579] text-[12px]">Sin marcas configuradas.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Mapping dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#f8f6f1]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Mapeo de producto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Marca</Label>
              <Select value={form.brand} onValueChange={v=>setForm({...form,brand:v})}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Producto maestro</Label>
              <Select value={form.master_id} onValueChange={v=>setForm({...form,master_id:v})}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>{master.map(ft => <SelectItem key={ft.id} value={ft.id}>{ft.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre comercial en {form.brand}</Label>
              <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder='Ej. "Premium Plus"' className="mt-1 bg-white"/>
              <p className="text-[11px] text-[#8a8579] mt-1">El producto maestro <span className="font-medium">{master.find(f=>f.id===form.master_id)?.name}</span> se muestra como "{form.name || '…'}" cuando se captura en una estación {form.brand}.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="save-mapping-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add brand dialog */}
      <Dialog open={openBrand} onOpenChange={setOpenBrand}>
        <DialogContent className="max-w-md bg-[#f8f6f1]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Nueva marca</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre</Label>
              <Input
                value={newBrand}
                onChange={e=>setNewBrand(e.target.value)}
                placeholder="Ej. Petrolatina"
                className="mt-1 bg-white"
                data-testid="new-brand-input"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') addBrand(); }}
              />
              <p className="text-[11px] text-[#8a8579] mt-1">Las marcas se usan al crear estaciones y para mapear los nombres comerciales por producto.</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={newBrandOwn} onChange={e => setNewBrandOwn(e.target.checked)}
                className="w-4 h-4 accent-[#1c1c1c]" data-testid="new-brand-own-checkbox"/>
              <span className="text-[12.5px]">Es marca propia</span>
              <span className="text-[11px] text-[#8a8579]">(sus estaciones se marcan como propias)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBrand(false)}>Cancelar</Button>
            <Button onClick={addBrand} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="save-brand-btn">Crear marca</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
